import React from 'react';
import { ArrowDown, ArrowUp, Lock } from 'lucide-react';
import type { LayoutSaveState } from '../../hooks/useDashboardLayout';
import { NotificationSheet } from '../notifications/NotificationSheet';
import { DashboardLayout, KpiId, PRESET_BY_ID, PresetId, WIDGET_BY_ID, WidgetId, hiddenWidgets, matchingPreset, zoneOf } from './dashboardLayout';
import { DESKTOP_ONLY, ZONE_SHORT, canMobileNudge, mobileSections } from './dashboardEditing';
import { KpiChooser, PresetPicker, SaveStatus } from './PresetPicker';
import { Tag } from './dashboardUi';

/**
 * „Anpassen" am Handy: ein Blatt ueber der ganzen Seite mit den Vorlagen,
 * allen Bausteinen (Schalter + Pfeile in der gelesenen Reihenfolge), den
 * Kacheln und dem Speicherstatus. Kein Ziehen — alles ist ein Knopf von
 * mindestens 44 px.
 */

const Switch: React.FC<{ on: boolean; label: string; onChange: () => void }> = ({ on, label, onChange }) => (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={onChange} className="relative w-11 h-11 shrink-0 inline-flex items-center justify-center">
        <span className={`relative block w-9 h-5 rounded-full transition-colors ${on ? 'bg-blue-600' : 'bg-slate-300'}`}>
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : ''}`} />
        </span>
    </button>
);

const RowButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }> = ({ label, children, ...rest }) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        {...rest}
        className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-slate-600 active:bg-slate-100 disabled:opacity-30 [&>svg]:w-5 [&>svg]:h-5"
    >
        {children}
    </button>
);

const FixedRow: React.FC<{ title: string }> = ({ title }) => (
    <li className="flex items-center gap-2.5 min-h-[48px] px-3 py-2 rounded-xl border border-dashed border-slate-300 text-slate-500">
        <Lock className="w-4 h-4 shrink-0 text-slate-400" aria-hidden="true" />
        <span className="min-w-0">
            <span className="block text-[12px] font-semibold">{title}</span>
            <span className="block text-[11px]">Fester Abschnitt am Handy</span>
        </span>
    </li>
);

interface RowProps {
    id: WidgetId;
    layout: DashboardLayout;
    onToggle: (id: WidgetId, visible: boolean) => void;
    onNudge: (id: WidgetId, delta: -1 | 1) => void;
}

const WidgetRow: React.FC<RowProps> = ({ id, layout, onToggle, onNudge }) => {
    const w = WIDGET_BY_ID[id];
    const zone = zoneOf(layout, id);
    const on = zone !== null;
    return (
        <li className="flex items-center gap-1 min-h-[60px] pl-1 pr-1 py-1.5 bg-white rounded-xl border border-slate-200">
            <Switch on={on} label={`„${w.title}“ ${on ? 'ausblenden' : 'einblenden'}`} onChange={() => onToggle(id, !on)} />
            <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 min-w-0">
                    <span className={`text-sm font-semibold truncate ${on ? 'text-slate-800' : 'text-slate-500'}`}>{w.title}</span>
                    {DESKTOP_ONLY.has(id) && <Tag>nur am PC</Tag>}
                </span>
                <span className="block text-[11px] text-slate-500 truncate">
                    {zone ? `Am PC: ${ZONE_SHORT[zone]} · ` : ''}{w.description}
                </span>
            </span>
            {on && (
                <span className="flex items-center shrink-0">
                    <RowButton label={`„${w.title}“ nach oben`} disabled={!canMobileNudge(layout, id, -1)} onClick={() => onNudge(id, -1)}><ArrowUp /></RowButton>
                    <RowButton label={`„${w.title}“ nach unten`} disabled={!canMobileNudge(layout, id, 1)} onClick={() => onNudge(id, 1)}><ArrowDown /></RowButton>
                </span>
            )}
        </li>
    );
};

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">{children}</h3>
);

interface Props {
    open: boolean;
    onClose: () => void;
    layout: DashboardLayout;
    saveState: LayoutSaveState;
    onRetrySave: () => void;
    onChoosePreset: (id: PresetId) => void;
    onToggleKpi: (id: KpiId) => void;
    onToggleWidget: (id: WidgetId, visible: boolean) => void;
    onNudge: (id: WidgetId, delta: -1 | 1) => void;
}

export const MobileCustomizeSheet: React.FC<Props> = ({ open, onClose, layout, saveState, onRetrySave, onChoosePreset, onToggleKpi, onToggleWidget, onNudge }) => {
    const { top, rest } = mobileSections(layout);
    const hidden = hiddenWidgets(layout);
    const active = matchingPreset(layout);
    return (
        <NotificationSheet
            open={open}
            onClose={onClose}
            title="Dashboard anpassen"
            footer={
                <div className="flex items-center justify-between gap-3">
                    <SaveStatus state={saveState} onRetry={onRetrySave} />
                    <button type="button" onClick={onClose} className="h-12 px-5 rounded-xl bg-slate-900 text-white text-sm font-semibold active:bg-slate-800 shrink-0">
                        Fertig
                    </button>
                </div>
            }
        >
            <div className="px-4 py-4 space-y-6 bg-[#f4f7fb] min-h-full">
                <p className="text-[12px] text-slate-500">
                    Dein Aufbau wird auf dem Server gespeichert und gilt auf jedem Gerät – am PC in Spalten, hier untereinander.
                </p>

                <section aria-label="Vorlagen">
                    <Heading>Vorlagen</Heading>
                    <PresetPicker layout={layout} onChoose={onChoosePreset} stacked />
                    <p className="text-[11px] text-slate-500 mt-2">
                        {active ? `Vorlage „${PRESET_BY_ID[active].title}“ – unverändert.` : 'Eigene Anordnung – keine Vorlage entspricht genau deinem Aufbau.'}
                    </p>
                </section>

                <section aria-label="Bausteine">
                    <Heading>Bausteine</Heading>
                    <ul className="space-y-2">
                        {top.map(id => <WidgetRow key={id} id={id} layout={layout} onToggle={onToggleWidget} onNudge={onNudge} />)}
                        <FixedRow title="Standort und Suche" />
                        {rest.map(id => <WidgetRow key={id} id={id} layout={layout} onToggle={onToggleWidget} onNudge={onNudge} />)}
                        <FixedRow title="Zuletzt benutzt" />
                        {hidden.length > 0 && (
                            <li className="pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500" aria-hidden="true">Ausgeblendet</li>
                        )}
                        {hidden.map(id => <WidgetRow key={id} id={id} layout={layout} onToggle={onToggleWidget} onNudge={onNudge} />)}
                    </ul>
                </section>

                <section aria-label="Kacheln">
                    <Heading>Kacheln „Heute im Blick“</Heading>
                    <KpiChooser layout={layout} onToggle={onToggleKpi} large />
                </section>
            </div>
        </NotificationSheet>
    );
};
